import React, { useState } from 'react';
import { Box, Button } from 'components/mui';
import { format, parseISO } from 'date-fns';
import ConfirmationDialog from 'components/confirmationDialog/ConfirmationDialog';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import {
	CancelLotusBlossomMembership,
	LotusBlossomPurchase,
	ResumeLotusBlossomMembership,
} from 'api/lotusBlossom/lotusBlossomAPI';
import { ClientMembership } from '../libs/clients';

interface MembershipActionsProps {
	membership: ClientMembership;
	onChange: (updated: LotusBlossomPurchase) => void;
}

/**
 * Cancel or resume a live membership. Cancelling only ever stops renewal: the
 * member keeps the period they paid for, and Stripe ends the membership — and
 * the server drops them from the lists — when it runs out.
 *
 * Buttons only; the renewal notice beside them already says what will happen.
 */
function MembershipActions({ membership, onChange }: MembershipActionsProps) {
	const { notifySuccess, notifyError } = useNotificationProvider();
	const [confirming, setConfirming] = useState(false);
	const [busy, setBusy] = useState(false);

	// No period end means it has ended, so there is nothing to change; no id means
	// a backend that can't take the request.
	if (!membership.periodEnd || !membership.subscriptionId) return null;

	const { subscriptionId } = membership;
	const endDate = format(parseISO(membership.periodEnd), 'MMM d, yyyy');

	const run = async (
		change: (id: string) => Promise<LotusBlossomPurchase>,
		success: string
	) => {
		setBusy(true);
		try {
			onChange(await change(subscriptionId));
			notifySuccess(success);
			setConfirming(false);
		} catch (error) {
			notifyError(error instanceof Error ? error.message : 'Something went wrong.');
		} finally {
			setBusy(false);
		}
	};

	return (
		// The mobile client card toggles on click, and React bubbles events out of
		// the dialog's portal too, so nothing in here may reach it.
		<Box onClick={(e) => e.stopPropagation()}>
			{membership.cancelAtPeriodEnd ? (
				<Button
					size="small"
					disabled={busy}
					onClick={() =>
						run(ResumeLotusBlossomMembership, 'Membership resumed — it will renew as normal.')
					}
				>
					{busy ? 'Resuming…' : 'Resume membership'}
				</Button>
			) : (
				<Button size="small" color="error" onClick={() => setConfirming(true)}>
					Cancel membership
				</Button>
			)}

			<ConfirmationDialog
				open={confirming}
				onClose={() => setConfirming(false)}
				onConfirm={() =>
					run(CancelLotusBlossomMembership, `Membership cancelled — it ends ${endDate}.`)
				}
				title="Cancel membership?"
				description={`They won't be charged again. The membership stays active until ${endDate}, then ends and they're removed from upcoming sessions. You can resume it any time before then.`}
				confirmText="Cancel membership"
				cancelText="Keep membership"
				loading={busy}
			/>
		</Box>
	);
}

export default MembershipActions;
